import { useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useAssetStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { WebsiteSelector } from './website-selector'
import { AdAccountsSelector } from './ad-accounts-selector'
import { CampaignsSelector } from './campaigns-selector'
import { AdAccountsSelectorSingle } from './ad-accounts-selector-single'
import { CampaignsSelectorSingle } from './campaigns-selector-single'
import { resolveBreadcrumbConfig } from './page-config'
import { AppButton } from '@clickguard/design-system'
import ChevronRight from '@untitled-ui/icons-react/build/esm/ChevronRight'
import XClose from '@untitled-ui/icons-react/build/esm/XClose'
import { AdAccountDTO } from 'api-models'
import { CampaignDTO, getAllCampaignIds } from './campaign-status'

const writeAdAccountIds = (params: URLSearchParams, ids: string[]) => {
	if (ids.length === 0) {
		params.delete('adAccounts')
	} else {
		params.set('adAccounts', ids.join(','))
	}
	// legacy keys
	params.delete('adAccount')
}

const writeCampaignIds = (params: URLSearchParams, ids: string[]) => {
	if (ids.length === 0) {
		params.delete('campaigns')
	} else {
		params.set('campaigns', ids.join(','))
	}
	// legacy keys
	params.delete('breadCampaigns')
	params.delete('breadCampaign')
	params.delete('campaign')
}

const clearAdAccountAndCampaignParams = (params: URLSearchParams) => {
	params.delete('adAccounts')
	params.delete('adAccount')
	params.delete('campaigns')
	params.delete('breadCampaigns')
	params.delete('breadCampaign')
	params.delete('campaign')
}

export const NavbarBreadcrumbs = () => {
	const location = useLocation()
	const [searchParams, setSearchParams] = useSearchParams()

	const { asset, adAccounts, campaigns } = useAssetStore(
		useShallow((s) => ({
			asset: s.asset,
			adAccounts: s.adAccounts,
			campaigns: s.campaigns
		}))
	)

	const isAssetPage = location.pathname.includes('/asset/')
	const config = useMemo(
		() => resolveBreadcrumbConfig(location.pathname),
		[location.pathname]
	)

	// Read selected ad accounts from URL params (with legacy fallback)
	const selectedAdAccounts = useMemo(() => {
		const param =
			searchParams.get('adAccounts') || searchParams.get('adAccount')
		if (!param) return []
		const ids = param.split(',').map((i) => i.trim()).filter(Boolean)
		return adAccounts.filter((acc) => ids.includes(acc.id))
	}, [searchParams, adAccounts])

	// Campaigns scoped to selected ad accounts (for the dropdown options)
	const scopedCampaigns = useMemo(() => {
		if (selectedAdAccounts.length === 0) return campaigns
		const adAccountIds = selectedAdAccounts.map((a) => a.id)
		return campaigns.filter((c) => adAccountIds.includes(c.ad_account_id))
	}, [selectedAdAccounts, campaigns])

	const hasCampaignParam = !!(
		searchParams.get('campaigns') ||
		searchParams.get('breadCampaigns') ||
		searchParams.get('breadCampaign') ||
		searchParams.get('campaign')
	)

	const selectedCampaigns = useMemo(() => {
		const param =
			searchParams.get('campaigns') ||
			searchParams.get('breadCampaigns') ||
			searchParams.get('breadCampaign') ||
			searchParams.get('campaign')
		if (!param) {
			// Fresh page load with no explicit selection: the multi-select
			// defaults to all campaigns, regardless of status (DEV-2437 —
			// single-select stays empty).
			return config.campaign === 'multi' ? scopedCampaigns : []
		}
		const ids = param.split(',').map((i) => i.trim()).filter(Boolean)
		return scopedCampaigns.filter((c) => ids.includes(c.id))
	}, [searchParams, scopedCampaigns, config.campaign])

	const selectedAdAccountSingle = selectedAdAccounts[0] ?? null
	const selectedCampaignSingle = selectedCampaigns[0] ?? null

	const handleAdAccountsApplyMulti = (accounts: AdAccountDTO[]) => {
		const newParams = new URLSearchParams(window.location.search)
		// "all selected" is equivalent to no filter
		const ids =
			accounts.length === 0 || accounts.length === adAccounts.length
				? []
				: accounts.map((a) => a.id)
		writeAdAccountIds(newParams, ids)
		setSearchParams(newParams, { replace: true })
	}

	const handleCampaignsApplyMulti = (selected: CampaignDTO[]) => {
		const newParams = new URLSearchParams(window.location.search)
		const allIds = getAllCampaignIds(scopedCampaigns)
		const selectedIds = getAllCampaignIds(selected)
		// Clearing the param represents the default all-campaigns scope
		// (DEV-2437). Any narrower selection is written out explicitly.
		const isAllDefault =
			selectedIds.length === allIds.length &&
			selectedIds.every((id) => allIds.includes(id))
		writeCampaignIds(newParams, isAllDefault ? [] : selectedIds)
		setSearchParams(newParams, { replace: true })
	}

	const handleAdAccountSelectSingle = (acc: AdAccountDTO) => {
		const newParams = new URLSearchParams(window.location.search)
		writeAdAccountIds(newParams, [acc.id])
		// switching ad account invalidates any existing campaign selection
		writeCampaignIds(newParams, [])
		setSearchParams(newParams, { replace: true })
	}

	const handleCampaignSelectSingle = (c: CampaignDTO) => {
		const newParams = new URLSearchParams(window.location.search)
		writeCampaignIds(newParams, [c.id])
		setSearchParams(newParams, { replace: true })
	}

	const handleClear = () => {
		const newParams = new URLSearchParams(window.location.search)
		clearAdAccountAndCampaignParams(newParams)
		setSearchParams(newParams, { replace: true })
	}

	// "Clear" button visibility:
	// - Multi-select: show when user has narrowed below "all"
	// - Single-select: show when any concrete selection exists
	const showClear =
		(config.adAccount === 'multi' && selectedAdAccounts.length > 0) ||
		(config.adAccount === 'single' && !!selectedAdAccountSingle) ||
		(config.campaign === 'multi' && hasCampaignParam) ||
		(config.campaign === 'single' && !!selectedCampaignSingle)

	const showAdAccount = isAssetPage && config.adAccount !== 'hidden'
	const showCampaign =
		isAssetPage && config.campaign !== 'hidden' && scopedCampaigns.length > 0

	return (
		<div className="flex items-center gap-2 min-w-0">
			<WebsiteSelector asset={asset} />

			{showAdAccount && (
				<>
					<ChevronRight className="size-4 shrink-0 text-[var(--text-tertiary,#98969a)]" />
					{config.adAccount === 'multi' ? (
						<AdAccountsSelector
							adAccounts={adAccounts}
							selectedAdAccounts={selectedAdAccounts}
							onApply={handleAdAccountsApplyMulti}
						/>
					) : (
						<AdAccountsSelectorSingle
							adAccounts={adAccounts}
							selectedAdAccount={selectedAdAccountSingle}
							onSelect={handleAdAccountSelectSingle}
							disabledPredicate={
								config.adAccountFilter === 'gads-only'
									? (acc) => acc.platform !== 'GADS'
									: undefined
							}
						/>
					)}
				</>
			)}

			{showCampaign && (
				<>
					<ChevronRight className="size-4 shrink-0 text-[var(--text-tertiary,#98969a)]" />
					{config.campaign === 'multi' ? (
						<CampaignsSelector
							campaigns={scopedCampaigns}
							selectedCampaigns={selectedCampaigns}
							adAccounts={adAccounts}
							onApply={handleCampaignsApplyMulti}
						/>
					) : (
						<CampaignsSelectorSingle
							campaigns={scopedCampaigns}
							selectedCampaign={selectedCampaignSingle}
							adAccounts={adAccounts}
							onSelect={handleCampaignSelectSingle}
						/>
					)}
				</>
			)}

			{(showAdAccount || showCampaign) && showClear && (
				<AppButton
					variant="ghost"
					size="sm"
					leftIcon={<XClose className="size-3.5" />}
					label="Clear"
					onClick={handleClear}
					className="ml-1"
				/>
			)}
		</div>
	)
}
